/**
 * G2eGente - Autoavaliação (App Mobile Funcionário)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { employeeSelfAPI, performanceReviewsAPI, performanceCyclesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  Star,
  ChevronLeft,
  ChevronRight,
  Check,
  Save,
  Brain,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const SCORE_OPTIONS = [
  { value: 1, label: 'Abaixo' },
  { value: 2, label: 'Parcial' },
  { value: 3, label: 'Atende' },
  { value: 4, label: 'Supera' },
  { value: 5, label: 'Excepcional' }
];

export default function EmployeeSelfEvaluation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cycleId = searchParams.get('cycle');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cycle, setCycle] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [step, setStep] = useState(0); // 0: intro, 1: tecnicas, 2: comportamentais, 3: feedback, 4: review
  
  const [tecnicas, setTecnicas] = useState([]);
  const [comportamentais, setComportamentais] = useState([]);
  const [pontosFortes, setPontosFortes] = useState(['']);
  const [pontosMelhoria, setPontosMelhoria] = useState(['']);
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    fetchData();
  }, [cycleId]);

  const fetchData = async () => {
    try {
      // Get pending evaluations to find cycle info
      const evalRes = await employeeSelfAPI.getMyEvaluations();
      const pending = evalRes.data.pending_self_evaluations || [];
      
      let targetCycle = null;
      if (cycleId) {
        targetCycle = pending.find(p => p.cycle_id === cycleId);
      } else if (pending.length > 0) {
        targetCycle = pending[0];
      }
      
      if (!targetCycle) {
        toast.error('Nenhuma autoavaliação pendente');
        navigate('/m/employee/evaluations');
        return;
      }
      
      setCycle(targetCycle);
      
      // Get templates
      const templatesRes = await performanceReviewsAPI.getTemplates();
      setTemplates(templatesRes.data);
      
      // Initialize with empty scores
      setTecnicas(templatesRes.data.competencias_tecnicas.map(c => ({
        nome: c.nome,
        pontuacao: null,
        observacao: ''
      })));
      setComportamentais(templatesRes.data.competencias_comportamentais.map(c => ({
        nome: c.nome,
        pontuacao: null,
        observacao: ''
      })));
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/m/employee/evaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (type, index, value) => {
    if (type === 'tecnica') {
      const updated = [...tecnicas];
      updated[index].pontuacao = value;
      setTecnicas(updated);
    } else {
      const updated = [...comportamentais];
      updated[index].pontuacao = value;
      setComportamentais(updated);
    }
  };

  const handleSubmit = async () => {
    const tecnicasValidas = tecnicas.filter(t => t.pontuacao);
    const comportamentaisValidas = comportamentais.filter(c => c.pontuacao);
    
    if (tecnicasValidas.length < 3 || comportamentaisValidas.length < 3) {
      toast.error('Avalie pelo menos 3 competências de cada tipo');
      return;
    }
    
    setSaving(true);
    try {
      await employeeSelfAPI.createSelfEvaluation({
        cycle_id: cycle.cycle_id,
        employee_id: '', // Backend will use logged user's employee
        tipo: 'autoavaliacao',
        competencias_tecnicas: tecnicasValidas,
        competencias_comportamentais: comportamentaisValidas,
        pontos_fortes: pontosFortes.filter(p => p.trim()),
        pontos_melhoria: pontosMelhoria.filter(p => p.trim()),
        comentario_geral: comentario,
        status: 'finalizado'
      });
      
      toast.success('Autoavaliação enviada com sucesso!');
      navigate('/m/employee/evaluations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const tecnicasCompletas = tecnicas.filter(t => t.pontuacao).length;
  const comportamentaisCompletas = comportamentais.filter(c => c.pontuacao).length;
  const progress = ((tecnicasCompletas + comportamentaisCompletas) / (tecnicas.length + comportamentais.length)) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employee-self-evaluation">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee/evaluations')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Autoavaliação</h1>
            <p className="text-sm text-slate-500">{cycle?.cycle_nome}</p>
          </div>
        </div>
        <Progress value={progress} className="h-1" />
      </header>

      <main className="p-4">
        {/* Step 0: Intro */}
        {step === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-cyan-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Avalie seu Desempenho
              </h2>
              <p className="text-slate-600 mb-4">
                Esta é sua oportunidade de refletir sobre seu trabalho no período de {cycle?.periodo}.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Avalie suas competências técnicas e comportamentais com sinceridade.
                Sua autoavaliação será comparada com a avaliação do seu gestor.
              </p>
              <Button 
                onClick={() => setStep(1)}
                className="bg-cyan-600 hover:bg-cyan-700"
                size="lg"
              >
                Começar Avaliação
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Competências Técnicas */}
        {step === 1 && (
          <div className="space-y-4">
            <Card className="border-cyan-200 bg-cyan-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                  <h2 className="font-semibold text-cyan-900">Competências Técnicas</h2>
                </div>
                <p className="text-sm text-cyan-700 mt-1">
                  Avalie suas habilidades técnicas relacionadas ao seu cargo
                </p>
                <Badge className="mt-2 bg-cyan-200 text-cyan-800">
                  {tecnicasCompletas}/{tecnicas.length} avaliadas
                </Badge>
              </CardContent>
            </Card>

            {tecnicas.map((comp, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-slate-900 mb-3">{comp.nome}</h4>
                  <div className="flex gap-2 mb-2">
                    {SCORE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleScoreChange('tecnica', index, opt.value)}
                        className={`flex-1 p-2 rounded-lg border-2 text-xs transition-all ${
                          comp.pontuacao === opt.value
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Star className={`w-4 h-4 mx-auto ${comp.pontuacao === opt.value ? 'fill-cyan-500' : ''}`} />
                        <span className="block mt-1">{opt.value}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    {SCORE_OPTIONS.find(o => o.value === comp.pontuacao)?.label || 'Selecione uma nota'}
                  </p>
                </CardContent>
              </Card>
            ))}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(0)} className="flex-1">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                disabled={tecnicasCompletas < 3}
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Competências Comportamentais */}
        {step === 2 && (
          <div className="space-y-4">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-green-600" />
                  <h2 className="font-semibold text-green-900">Competências Comportamentais</h2>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  Avalie suas habilidades interpessoais e comportamentais
                </p>
                <Badge className="mt-2 bg-green-200 text-green-800">
                  {comportamentaisCompletas}/{comportamentais.length} avaliadas
                </Badge>
              </CardContent>
            </Card>

            {comportamentais.map((comp, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-slate-900 mb-3">{comp.nome}</h4>
                  <div className="flex gap-2 mb-2">
                    {SCORE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleScoreChange('comportamental', index, opt.value)}
                        className={`flex-1 p-2 rounded-lg border-2 text-xs transition-all ${
                          comp.pontuacao === opt.value
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Star className={`w-4 h-4 mx-auto ${comp.pontuacao === opt.value ? 'fill-green-500' : ''}`} />
                        <span className="block mt-1">{opt.value}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    {SCORE_OPTIONS.find(o => o.value === comp.pontuacao)?.label || 'Selecione uma nota'}
                  </p>
                </CardContent>
              </Card>
            ))}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={comportamentaisCompletas < 3}
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Feedback */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Feedback Pessoal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-2">
                    Seus Pontos Fortes
                  </label>
                  {pontosFortes.map((p, i) => (
                    <input
                      key={i}
                      type="text"
                      value={p}
                      onChange={(e) => {
                        const updated = [...pontosFortes];
                        updated[i] = e.target.value;
                        setPontosFortes(updated);
                      }}
                      placeholder="Ex: Trabalho em equipe"
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                    />
                  ))}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setPontosFortes([...pontosFortes, ''])}
                    className="text-green-600"
                  >
                    + Adicionar
                  </Button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-2">
                    Pontos a Desenvolver
                  </label>
                  {pontosMelhoria.map((p, i) => (
                    <input
                      key={i}
                      type="text"
                      value={p}
                      onChange={(e) => {
                        const updated = [...pontosMelhoria];
                        updated[i] = e.target.value;
                        setPontosMelhoria(updated);
                      }}
                      placeholder="Ex: Gestão do tempo"
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                    />
                  ))}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setPontosMelhoria([...pontosMelhoria, ''])}
                    className="text-amber-600"
                  >
                    + Adicionar
                  </Button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Comentário Geral (opcional)
                  </label>
                  <Textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Compartilhe suas reflexões sobre o período..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Enviar Avaliação
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
